import React, { FC, memo, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FaArrowDown, FaRegFile } from 'react-icons/fa';
import { Spinner } from 'react-bootstrap';
import ChatMessage from '../ChatMessage';
import ChatPanel from '../ChatPanel';
import SendMessageForm from '../SendMessageForm';
import SendFileForMessage from '../SendFileForMessage';
import ShowLoadFileInfo from '../ShowLoadFileInfo';
import Button from '../../../UIComponents/Button';
import { ConsumerMessage } from '../../message/message.types';
import { getLastMessage, groupMessagesByDate } from '../../message/message.utils';
import { useAppDispatch, useAppSelector } from '../../../hooks/defaultHooks';
import { getActualMessageChannel } from '../../../selectors/selectors';
import { channelState } from '../../../store/slices/messageChannelSlice';
import { openOffcanvas } from '../../../store/slices/offcanvasSlice';
import cls from './ChatContainer.module.scss';

type ChatContainerProps = {
  consumerId: string;
  messages: ConsumerMessage[];
  isLoading: boolean;
  isLoadingOlderMessages: boolean;
  hasMoreMessages: boolean;
  loadOlderMessages: () => void;
};

/** How far from the bottom the chat still counts as "scrolled to the bottom", in px. */
const BOTTOM_THRESHOLD = 40;

const readStoredChannel = (): channelState | undefined => {
  const stored = localStorage.getItem('activeChannel');
  return stored ? (JSON.parse(stored) as channelState) : undefined;
};

const ChatContainer: FC<ChatContainerProps> = memo(
  ({
    consumerId,
    messages,
    isLoading,
    isLoadingOlderMessages,
    hasMoreMessages,
    loadOlderMessages,
  }) => {
    const dispatch = useAppDispatch();
    const activeChannel = useAppSelector(getActualMessageChannel);
    const channelData = activeChannel.email ? activeChannel : readStoredChannel();
    const bodyRef = useRef<HTMLDivElement>(null);
    const [showScrollButton, setShowScrollButton] = useState<boolean>(false);
    const [showLoadFileInfo, setShowLoadFileInfo] = useState<boolean>(false);
    const [fileInfo, setFileInfo] = useState<{ name: string }>({
      name: '',
    });
    // scroll anchors of the previous render, see the layout effect below
    const scrollAnchor = useRef({ firstId: '', lastId: '', scrollHeight: 0, isAtBottom: true });

    const messagesGroups = useMemo(() => groupMessagesByDate(messages), [messages]);
    const finalizeMessage = getLastMessage(messages)?.finalMessage ?? false;

    const scrollToBottom = () => {
      bodyRef.current?.scrollTo({
        top: bodyRef.current.scrollHeight,
        behavior: 'smooth',
      });
    };
    const scrollHandler = () => {
      const body = bodyRef.current;
      if (!body) {
        return;
      }
      const distanceToBottom = body.scrollHeight - body.scrollTop - body.clientHeight;
      const isAtBottom = distanceToBottom <= BOTTOM_THRESHOLD;
      scrollAnchor.current.isAtBottom = isAtBottom;
      setShowScrollButton(!isAtBottom);
    };
    const openConsumerOffcanvas = (id: string) => {
      dispatch(openOffcanvas({ type: 'consumerOffcanvas', extra: { id } }));
    };

    // Keeps the viewport where the reader expects it: pinned to the bottom while
    // new messages arrive, and on the same message when an older page is prepended.
    useLayoutEffect(() => {
      const body = bodyRef.current;
      if (!body) {
        return;
      }
      const previous = scrollAnchor.current;
      const firstId = messages[0]?.id ?? '';
      const lastId = getLastMessage(messages)?.id ?? '';
      const isFirstRenderWithMessages = !previous.lastId && Boolean(lastId);

      if (isFirstRenderWithMessages || (lastId !== previous.lastId && previous.isAtBottom)) {
        body.scrollTop = body.scrollHeight;
      } else if (previous.firstId && firstId !== previous.firstId) {
        body.scrollTop += body.scrollHeight - previous.scrollHeight;
      }
      scrollAnchor.current = {
        firstId,
        lastId,
        scrollHeight: body.scrollHeight,
        isAtBottom: previous.isAtBottom,
      };
    }, [messages]);

    return (
      <div className={cls.Container} data-testid="chat-container">
        <header className={cls.Header}>
          <div onClick={() => openConsumerOffcanvas(consumerId)} className={cls.Info}>
            {channelData?.name && <h5 className={`title-5 ${cls.Title}`}>{channelData?.name}</h5>}
            <p className={channelData?.name ? cls.Email : `title-5 ${cls.Title}`}>
              {channelData?.email}
            </p>
          </div>
          <ChatPanel id={consumerId} finalizeMessage={finalizeMessage} />
        </header>
        <div className={cls.Body} data-testid="chat-section" ref={bodyRef} onScroll={scrollHandler}>
          {hasMoreMessages && (
            <Button
              text={isLoadingOlderMessages ? 'Loading...' : 'Load more'}
              clickHandler={loadOlderMessages}
              disabled={isLoadingOlderMessages}
              className={cls.LoadMoreButton}
            />
          )}
          {isLoading && <Spinner className="align-self-center" size="sm" color="currentColor" />}
          {messagesGroups.map((group) => (
            <div key={group.date} className={cls.GroupDate}>
              <h3 className={cls.Date}>{group.date}</h3>
              {group.messages.map((message) => (
                <div
                  key={message.id}
                  className={`chat__message-container  chat__message-container--${message.author.toLocaleLowerCase()}`}
                  data-testid="message-container"
                >
                  <ChatMessage
                    message={message}
                    consumerEmail={channelData?.email as string}
                    consumerName={channelData?.name as string}
                  />
                </div>
              ))}
            </div>
          ))}
          {showScrollButton && (
            <button className={cls.ScrollBottom} onClick={scrollToBottom}>
              <FaArrowDown />
            </button>
          )}
        </div>
        {showLoadFileInfo && (
          <ShowLoadFileInfo>
            <div className={cls.LoadFileInfo}>
              <div className={cls.LoadFile}>
                <div className={cls.LoadFileIcon}>
                  <FaRegFile size="15px" />
                </div>
                <span className={cls.LoadFileText}>{fileInfo.name}</span>
              </div>
              <Spinner size="sm" color="currentColor" />
            </div>
          </ShowLoadFileInfo>
        )}
        {!showLoadFileInfo && (
          <div className={cls.Footer}>
            <SendFileForMessage
              consumerId={consumerId}
              setShowFileLoad={setShowLoadFileInfo}
              setFileInfo={setFileInfo}
            />
            <SendMessageForm consumerId={consumerId} />
          </div>
        )}
      </div>
    );
  },
);

export default ChatContainer;
